import React, { useState, useEffect, useRef } from "react";
import reactWrapper from "@harbor/elements/utils/react/wrapper";
import { PropTypes } from "prop-types";
import _ from "lodash";

//utils
import { displayDateTimeSeconds } from "../../../../utils/displayTime";
import { getKeyValueStyle } from "../../../../utils/common";
import { getCookie } from "../../../../serviceWorker";
import { DEVICE_STATUS_URL, getDeviceStatusURL } from "../../../../apis/apiConstants";
import { getTaskResult } from "./taskStatus";

//config
import apiService from "../../../../config/api-config";

//components
import NoDataAvailable from "../../../../common/NoDataAvailable";
import { useI18n } from "../../../../i18n/useI18n";

const [
    HbrTabPanel,
    HbrInput,
    HbrIcon,
] = reactWrapper([
    "hbr-tab-panel", "hbr-input", "hbr-icon"
]);

const COMPLETED_TASK = "completed";
const REPORT_GENERATE_ACTIVITY = "report_generate";

const Task = ({ name }) => {
    const { tGenerics } = useI18n('generics');
    const [data, setData] = useState();
    const [timeNow, setTimeNow] = useState(new Date());

    const dataRef = useRef({
        active: [],
        completed: [],
        error: null
    })

    const refresh = () => {
        setTimeNow(new Date());
    }

    const getData = async () => {
        const result = await apiService.getTaskSummary();

        if (result.errorObject instanceof Object === false) {
            dataRef.current.active = result.data.data.filter(d => d.status !== "done")
            dataRef.current.completed = result.data.data.filter(d => d.status === "done")

            if (name === "active") {
                setData(dataRef.current.active);
            } else {
                setData(dataRef.current.completed)
            }
        }


    }

    useEffect(() => {
        getData();
    }, [timeNow])

    const onInput = (e) => {
        const currentValue = e.currentTarget.value;
        if (!_.isEmpty(currentValue)) {
            setData(dataRef.current[name].filter((value) => {
                return (
                    value.name?.toString().toLowerCase().includes(currentValue) ||
                    value.userSessionUserName?.toString().toLowerCase().includes(currentValue) ||
                    value.userSessionIP?.toString().toLowerCase().includes(currentValue) ||
                    value.tenantName?.toString().toLowerCase().includes(currentValue) ||
                    value.total?.toString().toLowerCase().includes(currentValue)
                )
            })
            );
        } else {
            setData(dataRef.current[name])
        }
    }

    const onRefreshClick = () => {
        refresh();
    }

    const removeTask = (e) => {
        e.stopPropagation();
        setData(data.filter((value) => value.startTime.toString() !== e.currentTarget.id));
    }

    const canRedirectToTaskStatus = (task) =>
        name === COMPLETED_TASK && !_.isEmpty(task?.processId);

    const getCompletedTaskRedirectUrl = (processId) => {
        const queryParam = `?activity=${encodeURIComponent(REPORT_GENERATE_ACTIVITY)}&pid=${encodeURIComponent(processId)}`;
        const analyticsCookie = getCookie("cl-analytics");

        if (_.isEmpty(analyticsCookie)) {
            return `${DEVICE_STATUS_URL}${queryParam}`;
        }

        try {
            const parsedCookie = JSON.parse(analyticsCookie);
            if (parsedCookie?.tenancyMode === "tenant" && !_.isEmpty(parsedCookie?.tenantId)) {
                return `${getDeviceStatusURL(parsedCookie.tenantId)}${queryParam}`;
            }
        } catch (error) {
            console.warn("Failed to parse cl-analytics cookie, using default URL", error);
        }

        return `${DEVICE_STATUS_URL}${queryParam}`;
    }

    const redirectToTaskStatus = (processId) => {
        if (_.isEmpty(processId)) return;
        // Force full-page navigation because the task status route is served from a different origin.
        // Full page reload required for vManage navigation
        window.location.href = getCompletedTaskRedirectUrl(processId);
    }

    const onTaskClick = (task) => {
        if (!canRedirectToTaskStatus(task)) return;
        redirectToTaskStatus(task.processId);
    }

    const onTaskKeyDown = (event, task) => {
        if (!canRedirectToTaskStatus(task)) return;
        if (event.key === "Enter" || event.key === " " || event.key === "Spacebar") {
            event.preventDefault();
            redirectToTaskStatus(task.processId);
        }
    }

    return (
        <HbrTabPanel name={name}>
            <div className="search-field">
                <div className="hbr-css__layout-col-md">
                    <HbrInput label="" placeholder={tGenerics('taskSearchPlaceholder')} size="medium" value="" clearable={true}
                        className="hbr-css__form-section-md"
                        data-testid="activated"
                        onHbr-input={onInput}
                    >
                        <HbrIcon slot="prefix" name="magnifying-glass" size="16px" staticColors={true} />
                        <HbrIcon slot="clear-icon" name="hbr-delete-content" size="16px" staticColors={true}></HbrIcon>
                    </HbrInput>
                </div>
            </div>
            <div className="task-one-row">
                <div className="key-value">
                    <div className="label hbr-type-body2">{tGenerics('taskLastUpdated')}</div>
                    <div className="time hbr-type-body2">{displayDateTimeSeconds(timeNow)}</div>
                </div>
                <div id="refresh-icon"><HbrIcon sentiment="info" name="refresh" size="20px" onClick={onRefreshClick}></HbrIcon></div>
            </div>
            <div className="task-sort">
                <div className="label hbr-type-body2">{tGenerics('taskSortBy')}</div>
                <div className="time hbr-type-body2">{tGenerics('taskStartTime')}</div>
            </div>
            {data?.length === 0 && <div className="no-data">
                <NoDataAvailable />
            </div>}
            <div className="sorted-tasks">
                {data && data.map((task, key) => {
                    const taskTitle = `${task.name} (${tGenerics('taskTotal', { taskTotal: task.total })})`;
                    const isTaskTitleClickable = canRedirectToTaskStatus(task);
                    const taskResult = getTaskResult(task);

                    return <div
                        className={`task${isTaskTitleClickable ? " clickable" : ""}`}
                        key={key}
                        onClick={isTaskTitleClickable ? () => onTaskClick(task) : undefined}
                        onKeyDown={isTaskTitleClickable ? (event) => onTaskKeyDown(event, task) : undefined}
                        role={isTaskTitleClickable ? "link" : undefined}
                        tabIndex={isTaskTitleClickable ? 0 : undefined}
                        aria-label={taskTitle}
                    >
                        <div className="task-one-row">
                            <div className="key-value">
                                <HbrIcon name={taskResult.icon} sentiment={taskResult.sentiment} size="18px"></HbrIcon>
                                <div
                                    className={`task-title hbr-type-body2${isTaskTitleClickable ? " clickable" : ""}`}
                                >
                                    {taskTitle}
                                </div>
                            </div>
                            <div className="remove-task" id={task.startTime} onClick={removeTask}><HbrIcon name="x" size="12px"></HbrIcon></div>
                        </div>
                        {taskResult.countEntries.map(([countKey, countValue]) =>
                            <React.Fragment key={countKey}>
                                {getKeyValueStyle(`${countKey}:`, countValue)}
                            </React.Fragment>
                        )}
                        {getKeyValueStyle(`${tGenerics('taskStart')}`, displayDateTimeSeconds(task.startTime))}
                        {getKeyValueStyle(`${tGenerics('taskEnd')}`, displayDateTimeSeconds(task.endTime))}
                        <div className="task-from">
                            {getKeyValueStyle(`${tGenerics('taskBy')}`, task.userSessionUserName)}
                            {getKeyValueStyle(`${tGenerics('taskFrom')}`, task.userSessionIP)}
                            {getKeyValueStyle(`${tGenerics('taskTenant')}`, task.tenantName)}
                        </div>
                    </div>
                })}
            </div>
        </HbrTabPanel>
    )
}

Task.propTypes = {
    name: PropTypes.string,
    updateTabsLength: PropTypes.func
}

export default Task;
